import { PixelIcon } from "../components/PixelArt";
import { Coins } from "../components/Gold";
import { PinnedNotes } from "../components/PinnedNotes";
import { MentionText } from "../components/MentionText";
import { useBoardFold } from "../board-fold";
import { cardRinging } from "../ring";
import { FlashBanner } from "../components/FlashBanner";
import { applyHouseholdFlags, crownedPortraitUrl, recapUIEnabled, useBountyUI, useClaimUI, useLocalFlag, useEconomyUI, useFocusUI, useNotesUI, useOriginalAssets, useQuickUI, useRecapUI, useStandingsUI, useWeeklyUI, useGalleryUI, usePeonMoodsUI, useAuctionUI, useRouletteUI, useYoursUI, useLateRushUI, useSpeedrunUI, useSlopTallyUI, useHouseGoalUI, useThemeWeekUI, useProjectsUI } from "../features";
import { asBountyTask } from "../../shared/projects.ts";
import type { SpeedrunNote } from "../../shared/speedrun";
import type { SlopTally } from "../../shared/slop";
import type { HouseWeekSummary } from "../../shared/house-week.ts";
import { offerGoal } from "../goal-sheet";
import { offerRecap, recapOf, showsRecapControl, watchRecapLabel, type RecapSummary } from "../recap";
import { warmRecap, watchRecap } from "../components/Recap";
import { useMinuteTick } from "../useMinuteTick";
import { useKnownLook } from "../appearance";
import { PeonLayers } from "../components/PeonLayers";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren, RefObject } from "preact";
import { api } from "../api";
import { hall } from "../hall-cache";
import { deviceTimezone } from "../store";
import { setAccountStats } from "../account-stats";
import { announceGoldRush } from "../audio";
import { takeLastAward, takeLastNotice } from "../award";
import { playBoardMoments } from "../tour-moments";
import { whatsNewBoardLoaded } from "../whats-new";
import { rouletteBoardLoaded } from "../roulette";
import { formatDoneAt } from "../../shared/format-done.ts";
import { setOpenCount } from "../openCount";
import { liftNew, pruneSeen, readSeen, unseenIds, writeSeen } from "../seen";
import { href, previousRouteName } from "../router";
import type { BoardSummary, FlashState, Note, Role, Task, TaskStatus, TopPeon } from "../../shared/types";
import { openTaskGold, reminderRule } from "../../shared/bounty.ts";
import { boostForTask, flashForFlags } from "../../shared/flash.ts";
import { showStandings, standingRows, standingsEmptyLine, type StandingRow } from "../../shared/standings.ts";
import { crownHeader, emptyCrownLine, type CrownHeader } from "../../shared/crown.ts";
import { isLuxuryTier } from "../../shared/economy.ts";
import { CARDS_PER_FRAME, FAN_MS, PILE_SHADE, PILE_SIZE, judgePull, partitionBoard, pileGeometry, pileMore, pileMoreLabel, shouldRestack, watchOverscroll, worthReporting, type GatherBy, type PullReport } from "../../shared/nextUp.ts";
import { report } from "../telemetry";
import { onScreenTop, scrollingToTop } from "../scroll-to-top";
import { useLive } from "../live";
import { recordBoard } from "../perf.ts";
import { boardRequestKey, startOfLastMonth, startOfThisMonth, takePrefetchedBoard } from "../prefetch";
import { recallBoard, rememberBoard, rememberBoardScroll, takeBoardScroll } from "../board-cache";
import { Empty, ErrorBox, Spinner, TabBar } from "../components/Shell";
import { BusyLabel } from "../components/BusyLabel";
import { AwardToast } from "../components/AwardToast";
import { taskCardLabel } from "../a11y-labels.ts";
import { ClaimHand } from "../components/ClaimHand";
import { PeonStamp } from "../components/PeonStamp";
import { cardRouletteWinner, rouletteStampLabel } from "../../shared/peon-stamp.ts";
import { activeClaim, claimFirstName } from "../../shared/claims.ts";
import { YOURS_HEADING, restHeading, splitYours } from "../../shared/yours.ts";
import { CrownViewer, DoneGallery, DoneViewToggle, EmptyBoard, useDoneView, useLongPress, useWeeklyScroll } from "../components/DoneExtras";
import { useWeeklyBoard } from "../components/WeeklyWriting";
import { SCROLL_GOLD_TOAST, galleryTasks } from "../../shared/weekly.ts";
import { offerWeekly, pendingWeekly, previewWeekly, useWeeklySheet, weeklyBoardLoaded } from "../weekly-sheet";
import { cheerBoardLoaded } from "../cheer-sheet";
import { boardFocusAction, type BoardFocus } from "../../shared/mentions.ts";

/** Matches the server's default page size (server/db.ts DONE_PAGE_SIZE); a returned page shorter than this means there is no more to fetch. */
const DONE_PAGE_SIZE = 50;

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Which phone a web pull came from, for the "pile.pull" telemetry event: the owner's may be an iPhone. */
function webPlatform(): string {
  try {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "web-ios";
    if (/Android/.test(ua)) return "web-android";
  } catch {
    /* no navigator */
  }
  return "web";
}

/**
 * Next up's pile (focusUI, owner's ruling 2026-09-29): the first three hand-added tasks as a small
 * deck under the chores (on the Done board, 2026-09-30: the three after the two most recent), the
 * top card whole and the two under it peeking out a strip of frame
 * each, a step darker. Pulling the list past its end spreads the cards with the finger, against a
 * rubber band (shared/nextUp.ts judgePull and spreadFor): pull far enough (COMMIT_PX) and it gives
 * way, the cards run the rest of the way into their places and `onFan` makes it an ordinary list;
 * let go sooner and they snap back. The pull is a shortcut, not the only door (2026-10-02 triage,
 * item 6): a tab under the top card ("3 more") fans the pile on a tap the same way a pull that
 * gave way does, and is the screen reader's button. A tap on the open tab, once its scroll to the
 * top is over, calls `onGather`, and the cards glide from where they were in the list back into
 * the stack; so does the board, through `gatherRef`, once the list is scrolled up until the second
 * card is out of view (owner's ruling 2026-09-30). The cards are the board's own TaskCards; only
 * the top one takes a tap. The pull writes CSS variables on the pile's own DOM (transforms and
 * opacity only), so the board never re-renders while a pull lasts.
 */
function NextUpPile({
  board,
  pile,
  count,
  fanned,
  onFan,
  onGather,
  gatherRef,
  scrollerRef,
  card,
}: {
  /** Which board the pile is on, for the "pile.pull" telemetry event. */
  board: TaskStatus;
  pile: Task[];
  /** Everything the pile holds, the tab's count (shared/nextUp.ts pileMore). */
  count: number;
  fanned: boolean;
  onFan: () => void;
  onGather: () => void;
  /** Set while fanned: gathers the pile as a tap on the open tab does, for the board's scroll check. */
  gatherRef: { current: ((by: GatherBy) => void) | null };
  scrollerRef: RefObject<HTMLElement>;
  card: (t: Task) => ComponentChildren;
}) {
  const pileRef = useRef<HTMLDivElement>(null);
  const handlers = useRef({ onFan, onGather });
  handlers.current = { onFan, onGather };
  // Where each card sat in the fanned list, read just before it gathers (the FLIP "first").
  const fannedTops = useRef<number[] | null>(null);
  const slots = () => Array.from(pileRef.current?.querySelectorAll<HTMLElement>(":scope > .pile-slot") ?? []);
  // Set while stacked: fans the pile from the tab, as a pull that gave way does.
  const fanFromTab = useRef<(() => void) | null>(null);

  // Stacked: a pull past the end spreads the pile, and far enough fans it out.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const el = pileRef.current;
    if (fanned || !scroller || !el) return;
    // The caption under the pile fades with the pull: it reads the same variable.
    const caption = el.nextElementSibling instanceof HTMLElement && el.nextElementSibling.classList.contains("pile-caption") ? el.nextElementSibling : null;
    const spread = (p: number) => {
      const v = p.toFixed(4);
      el.style.setProperty("--p", v);
      caption?.style.setProperty("--p", v);
    };
    let measured = false;
    let via = "touch";
    let fanTimer = 0;
    // Given way (a pull far enough, or the tab): the rest is the fan's, nothing more to judge.
    let given = false;
    const tell = (r: PullReport) => {
      if (worthReporting(r)) report("pile.pull", r.committed ? "committed" : "snapped", { board, platform: webPlatform(), via, travel: r.travel, ms: r.ms, committed: r.committed });
    };
    // Each card's place in the list from the heights it has now: what the pull spreads them
    // towards, so the list it ends in has every card exactly where the fan left it.
    const measure = () => {
      if (measured) return;
      measured = true;
      const all = slots();
      const g = pileGeometry(all.map((s) => s.offsetHeight));
      all.forEach((s, i) => s.style.setProperty("--fan", `${(g.fanned[i] ?? 0) - s.offsetTop}px`));
    };
    // The cards run the rest of the way into their places and the list takes over.
    const giveWay = () => {
      given = true;
      measure();
      delete el.dataset.pull;
      el.dataset.fan = "";
      spread(1);
      if (reducedMotion()) handlers.current.onFan();
      else fanTimer = window.setTimeout(() => handlers.current.onFan(), FAN_MS);
    };
    const judge = judgePull({
      onSpread(p) {
        if (given) return;
        if (!measured) {
          measure();
          el.dataset.pull = "";
        }
        spread(p);
      },
      onCommit(r) {
        if (given) return;
        tell(r);
        giveWay();
      },
      onSnapBack(r) {
        if (given) return;
        tell(r);
        measured = false;
        delete el.dataset.pull;
        spread(0);
      },
    });
    fanFromTab.current = () => {
      if (given) return;
      report("pile.fan", "tab", { board, platform: webPlatform() });
      giveWay();
    };
    const stop = watchOverscroll(scroller, {
      onPull(px, how) {
        via = how;
        judge.pull(px);
      },
      onRelease: () => judge.release(),
    });
    return () => {
      fanFromTab.current = null;
      stop();
      judge.dispose();
      window.clearTimeout(fanTimer);
      delete el.dataset.pull;
      delete el.dataset.fan;
      spread(0);
    };
  }, [fanned, scrollerRef, board]);

  // Fanned: a tap on the open tab gathers it again, once its scroll is over; so does scrolling up
  // until its second card is out of view (the board's scroll check calls `gatherRef`). Both the
  // same way: each card from where it sits in the list into the stack.
  useEffect(() => {
    if (!fanned) return;
    let done = false;
    const gatherBy = (by: GatherBy) => {
      if (done) return;
      done = true;
      report("pile.gather", by, { board, by, platform: webPlatform() });
      fannedTops.current = slots().map((s) => s.offsetTop);
      handlers.current.onGather();
    };
    gatherRef.current = gatherBy;
    const off = onScreenTop(() => gatherBy("tab"));
    return () => {
      off();
      if (gatherRef.current === gatherBy) gatherRef.current = null;
    };
  }, [fanned, board, gatherRef]);

  // Gathering: each card from where it was in the list to its place in the stack.
  useLayoutEffect(() => {
    const el = pileRef.current;
    const first = fannedTops.current;
    fannedTops.current = null;
    if (fanned || !el || !first || reducedMotion()) return;
    const all = slots();
    all.forEach((s, i) => {
      if (i === 0 || first[i] === undefined) return;
      s.style.transition = "none";
      s.style.transform = `translateY(${first[i]! - s.offsetTop}px)`;
    });
    void el.offsetHeight;
    all.forEach((s) => {
      s.style.transition = "";
      s.style.transform = "";
    });
  }, [fanned]);

  const n = pile.length;
  const more = fanned ? "" : pileMore(count);
  return (
    <div class="pile" ref={pileRef} data-fanned={fanned || undefined} data-cards={n} data-tab={more ? "" : undefined}>
      {pile.map((t, i) => (
        <div
          key={t.id}
          class={`pile-slot${!fanned && i > 0 ? " pile-under" : ""}`}
          style={{ "--i": i, "--below": n - 1 - i, "--shade": PILE_SHADE[i] ?? 0 }}
          aria-hidden={!fanned && i > 0 ? true : undefined}
          inert={!fanned && i > 0 ? true : undefined}
        >
          {card(t)}
        </div>
      ))}
      {/* The pull's visible door: a tab tucked under the stack, counting the cards under the top
          one. A tap fans the pile as a pull that gave way does; gone while fanned. */}
      {more && (
        <button type="button" class="pile-tab" aria-label={pileMoreLabel(count, board === "done" ? "done" : "open")} onClick={() => fanFromTab.current?.()}>
          <span class="pile-tab-face" aria-hidden="true">
            {more}
          </span>
        </button>
      )}
    </div>
  );
}

function TaskCard({ task, now, flash, isNew = false, peonName }: { task: Task; now: number; flash: FlashState | null; isNew?: boolean; peonName: string }) {
  const bountyUIEnabled = useBountyUI();
  // "Me do it" (claimUI): a live claim adds a tiny hand and the first name under the title, nothing else.
  const claimedBy = useClaimUI() ? activeClaim(task, now) : null;
  // The roulette wheel's pick wears their peon's stamp in the card's bottom-left corner (shared/peon-stamp.ts).
  const roulette = cardRouletteWinner(task, useRouletteUI());
  const rouletteLabel = roulette ? rouletteStampLabel(roulette, peonName) : null;
  const economyUIEnabled = useEconomyUI();
  const luxuryTier = economyUIEnabled && isLuxuryTier(task.luxury) ? task.luxury : null;
  const thumb = task.status === "done" ? task.done_photo_id : task.start_photo_id;
  // Only urgency colours a card now; age no longer does (order carries it, docs/product.md).
  const urgent = task.status === "open" && !!task.urgent;
  // A picked gold rush boosts its own task alone (shared/flash.ts boostForTask, docs/bounties.md "Gold rush pick").
  const boost = boostForTask(flash, task.id);
  // A rising luxury task's coins follow its current total while auctionUI is on (shared/bounty.ts openTaskGold).
  const auctionUIEnabled = useAuctionUI();
  // The late chore rush (lateRushUI): inside the viewer's own window every late chore's coins follow its double.
  const reminder = reminderRule(useLateRushUI(), flash?.late_rush);
  // A project (projectsUI, docs/projects.md) shows its pot, not an aged big job's gold.
  const projectsUIEnabled = useProjectsUI();
  const coins = bountyUIEnabled ? (task.status === "open" ? openTaskGold(asBountyTask(task, projectsUIEnabled), now, { boost, by: peonName, auction: auctionUIEnabled, reminder }) : (task.gold_awarded ?? 0)) : 0;
  const doneAt = task.status === "done" ? formatDoneAt(task.done_at, now) : "";
  // The card's name says what its colours and icons say ("Fix bins, repeats, urgent, a lot of gold"),
  // so the photo placeholder is not the first thing a screen reader hears; the parts are hidden.
  const label = taskCardLabel({
    title: task.title,
    isNew,
    repeats: !!task.schedule_id,
    urgent,
    luxury: !!luxuryTier,
    gold: task.status === "open" ? coins : 0,
    done: task.status === "done" ? { by: task.done_by, at: doneAt } : undefined,
    claimedBy,
    roulette: rouletteLabel,
  });
  return (
    <a class={`card task-card${urgent ? " urgent" : ""}${luxuryTier ? " luxury" : ""}`} href={href({ name: "task", id: task.id })} data-id={task.id} aria-label={label}>
      <div class="thumb" aria-hidden="true">
        {thumb ? <img src={api.photos.thumbUrl(thumb)} alt="" loading="lazy" /> : <span class="thumb-empty">No photo</span>}
      </div>
      <div class="task-card-body">
        <div class="task-title">
          {isNew && (
            <span class="pill new-pill" title="New since you last looked" aria-hidden="true">
              New
            </span>
          )}
          {task.schedule_id && (
            <span class="pill" title="Scheduled task" aria-hidden="true">
              <PixelIcon name="repeat" />
              <span class="sr-only">Repeats</span>
            </span>
          )}
          <MentionText text={task.title} />
        </div>
        {claimedBy && (
          <div class="task-meta task-claim" aria-hidden="true">
            <ClaimHand />
            <span>{claimFirstName(claimedBy)}</span>
          </div>
        )}
        {task.status === "done" && (
          // One line for one fact: who finished it and when. Open cards carry only the title;
          // time, size and the exact amount live on the task screen.
          <div class="task-meta task-meta-bounty">
            <span>
              Done by <strong><MentionText text={task.done_by ?? ""} /></strong> · {doneAt}
            </span>
          </div>
        )}
      </div>
      {/* The coins get their own slot on the right edge, vertically centred: the same spot on every card,
          never over the photo and never moving with the title's line count. */}
      {coins > 0 && (
        <span class="task-coins" aria-hidden="true">
          <Coins total={coins} />
        </span>
      )}
      {roulette && rouletteLabel && <PeonStamp name={roulette} label={rouletteLabel} className="card-stamp" />}
      {/* The "!" keeps the card's top-right corner (the bonus badge has the top-left); the coins sit
          below it, centred, so both fit without moving the coins off the shared column. */}
      {urgent && (
        <span class="bang" aria-hidden="true">
          <PixelIcon name="bang" />
        </span>
      )}
    </a>
  );
}

/**
 * The crown, on the Done tab only: portrait, title, winner and count. The header names last month's
 * winner, or this month's leader when last month was quiet (shared/crown.ts, docs/bounties.md "The
 * crown is last month's"); the standings under it stay this month's race. Your own work and gold
 * live behind your name now (src/components/AccountSheet.tsx), so this card carries no controls; the
 * season recap's replay sits under it as its sibling (RecapRow), never inside the link.
 */
function PeonOfTheMonth({ header, standings, loading = false }: { header: CrownHeader | null; standings: TopPeon[] | null; loading?: boolean }) {
  const originalAssets = useOriginalAssets();
  const standingsUIEnabled = useStandingsUI();
  const winner = header?.peon ?? null;
  // With the flag on the list area shows: one row per peon this month, or the empty line under a
  // named crown (never under the "up for grabs" line, which already says it).
  const listed = showStandings(standingsUIEnabled, standings, winner !== null) ? standingRows(standings) : null;
  // The crown is worn in the winner's shirt (owner, 2026-09-24) and over their hair, beard and
  // extras (2026-10-04); the summary carries them all.
  const look = useKnownLook(winner?.name ?? null, winner);
  // A long press (or a right click) shows the winner's look large (CrownViewer); a short press still
  // opens the Hall. Nothing to show with nobody crowned.
  const [viewing, setViewing] = useState(false);
  const long = useLongPress(winner ? () => setViewing(true) : null);
  return (
    <>
    <a
      class="potm"
      href={href({ name: "hall" })}
      {...long}
      // The Hall's request starts with the finger, not with the screen (shared/hall-memory.ts).
      onPointerDown={(e) => {
        long.onPointerDown(e);
        void hall.prefetch(deviceTimezone(), performance.now()).catch(() => {});
      }}
      aria-label={
        !listed
          ? "Peon of the Month. Opens the Hall of Fame"
          : listed.length
            ? `Peon of the Month. ${listed.map((r) => r.label).join(". ")}. Opens the Hall of Fame`
            : `Peon of the Month. ${standingsEmptyLine()} Opens the Hall of Fame`
      }
    >
      <span class="peon-stack">
        <img class="potm-portrait" src={crownedPortraitUrl(originalAssets, look.shirt)} alt="" />
        {/* The Warcraft II portrait wears no one's look. */}
        {originalAssets && winner && <PeonLayers look={look} crowned />}
      </span>
      <div class="potm-body">
        <div class="potm-title">Peon of the Month</div>
        {loading && !winner ? (
          // The card's final shape while the board loads, so the list below does not shift when
          // the winner arrives: a blank name line and a blank count line at their real heights.
          <>
            <div class="potm-name">{" "}</div>
            <div class="potm-count">{" "}</div>
          </>
        ) : winner ? (
          <>
            <div class="potm-name">{winner.name}</div>
            <div class="potm-count">{header!.countLine}</div>
          </>
        ) : (
          <div class="potm-count">{emptyCrownLine()}</div>
        )}
      </div>
      {listed && <Standings rows={listed} />}
      {/* Says where the card goes. Not a control of its own: the whole card is the tap. */}
      <span class="potm-hall" aria-hidden="true">Hall of Fame</span>
    </a>
    {viewing && winner && (
      <CrownViewer
        portrait={crownedPortraitUrl(originalAssets, look.shirt)}
        look={originalAssets ? look : null}
        name={winner.name}
        line={header!.countLine}
        onClose={() => setViewing(false)}
      />
    )}
    </>
  );
}

/**
 * The season recap's replay (src/recap.ts), drawn as the Peon of the Month card's last row: the card
 * is a link to the Hall and a control cannot sit inside it, so this is its sibling, tucked under it
 * in the same gold frame. A quiet control (docs/product.md: a quieter control is never a smaller one).
 */
function RecapRow({ recap }: { recap: RecapSummary }) {
  // The signed links the player loads (src/recap.ts), asked for now so a tap starts with them.
  useEffect(() => warmRecap(recap), [recap.id]);
  return (
    <div class="potm-recap">
      <span class="potm-recap-title" aria-hidden="true">{recap.title}</span>
      <button type="button" class="btn-quiet potm-recap-watch" aria-label={watchRecapLabel(recap)} onClick={() => watchRecap(recap)}>
        Watch recap
      </button>
    </div>
  );
}

/**
 * Monthly standings (docs/bounties.md "Monthly standings"): the month's top three under the winner,
 * across the card below an inset rule, gold first; with nobody yet this month, one muted line in the
 * list's place. Not a control: the whole card opens the Hall.
 */
function Standings({ rows }: { rows: StandingRow[] }) {
  return (
    <ol class="potm-standings">
      {rows.length === 0 && <li class="potm-standing potm-standing-empty">{standingsEmptyLine()}</li>}
      {rows.map((row) => (
        <li class="potm-standing" key={row.medal}>
          <img class="potm-medal" src={`/ui/medal-${row.medal}@3x.png`} width={12} height={12} alt="" aria-hidden="true" />
          <span class="potm-standing-name">{row.name}</span>
          <span class="potm-standing-number">{row.number}</span>
          <span class="potm-standing-unit">{row.unit}</span>
        </li>
      ))}
    </ol>
  );
}

/** Set once a pile has fanned on this device: the "pull past the end" hint is not needed again. */
const PILE_HINT_KEY = "op.pileHintSeen";
function readPileHintSeen(): boolean {
  try {
    return localStorage.getItem(PILE_HINT_KEY) === "1";
  } catch {
    return false;
  }
}
function writePileHintSeen(): void {
  try {
    localStorage.setItem(PILE_HINT_KEY, "1");
  } catch {
    /* private mode: the hint just shows again next time */
  }
}

export function Board({ status, peonName, role, focus = null }: { status: TaskStatus; peonName: string; role: Role; /** A name tag's link (docs/mentions.md): a note to open, or a week's scroll to show. */ focus?: BoardFocus | null }) {
  const bountyUIEnabled = useBountyUI();
  const notesUIEnabled = useNotesUI();
  const speedrunUIEnabled = useSpeedrunUI();
  const slopTallyUIEnabled = useSlopTallyUI();
  const houseGoalUIEnabled = useHouseGoalUI();
  const themeWeekUIEnabled = useThemeWeekUI();
  const economyUIEnabled = useEconomyUI();
  const focusUIEnabled = useFocusUI();
  const quickUIEnabled = useQuickUI();
  // The late chore rush rides in the gold rush's state (docs/bounties.md "Late chore rush"), so the
  // board asks for that state while either switch is on; flashForFlags keeps each part behind its own.
  const lateRushEnabled = useLateRushUI();
  // The October roadmap's Done and empty-board pieces (src/components/DoneExtras.tsx), each behind its flag.
  const weeklyUIEnabled = useWeeklyUI();
  const galleryUIEnabled = useGalleryUI();
  const peonMoodsEnabled = usePeonMoodsUI();
  // Yours (shared/yours.ts, docs/yours.md): the tasks the wheel assigned to you or that you are on,
  // first on the open board under their own heading; each half follows its own flag, as the card does.
  const yoursUIEnabled = useYoursUI();
  const claimUIEnabled = useClaimUI();
  const rouletteUIEnabled = useRouletteUI();
  // The weekly scroll lives on the open board once this device has seen it in its sheet
  // (src/weekly-sheet.ts); a newer week read here comes up in the sheet the same way.
  const weekly = useWeeklyScroll(status === "open" && weeklyUIEnabled);
  const weeklySheet = useWeeklySheet();
  useEffect(() => {
    if (weekly) offerWeekly(weekly);
  }, [weekly]);
  const [doneView, setDoneView] = useDoneView();
  const gallery = status === "done" && galleryUIEnabled && doneView === "gallery";
  // The first render of a board already holds what it showed last time (src/board-cache.ts): the
  // list, the crown, the notes, the rush banner and the Done paging. Initialised here, not in the
  // load effect (which runs after paint), so a tab switch never paints a frame of anything else.
  const remembered = recallBoard(status);
  const [tasks, setTasks] = useState<Task[] | null>(remembered?.tasks ?? null);
  // Next up's pile (the open board's hand-added tasks, or the Done board's record after its two most
  // recent): stacked on every visit; a pull far enough past the end, or its "3 more" tab, fans it
  // out, a tap on the open tab (back to the top) stacks it again. `restShown` is how many of the tasks after the pile are drawn so far:
  // they follow the fan out CARDS_PER_FRAME per frame, below everything on screen.
  const [fanned, setFanned] = useState(false);
  const [restShown, setRestShown] = useState(0);
  // Until a pile has fanned once on this device, a muted line under its caption says how.
  const [pileHintSeen, setPileHintSeen] = useState(readPileHintSeen);
  const fan = useCallback(() => {
    setFanned(true);
    writePileHintSeen();
    setPileHintSeen(true);
  }, []);
  const gather = useCallback(() => {
    setFanned(false);
    setRestShown(0);
  }, []);
  const [winner, setWinner] = useState<TopPeon | null>(remembered?.top ?? null);
  // Who the card's header names (shared/crown.ts); a board remembered without it reads `top` as this month's.
  const [crown, setCrown] = useState<BoardSummary["crown"]>(remembered?.crown);
  // The month's top three (docs/bounties.md "Monthly standings"); a board remembered without them reads as none.
  const [standings, setStandings] = useState<TopPeon[] | null>(remembered?.standings ?? null);
  // The season recap the summary carries (src/recap.ts); a board remembered before the field existed has none.
  const [recap, setRecap] = useState<RecapSummary | null>(recapOf(remembered));
  const recapUI = useRecapUI();
  // This device's own "Season recap" switch: every request then carries x-flag-preview (src/api.ts),
  // and flipping it loads the board again, so the summary that brings the recap (and the
  // interstitial, once the workshop closes) arrives without reopening the app.
  const recapPreview = useLocalFlag("recapUI");
  const [flash, setFlash] = useState<FlashState | null>(remembered && status === "open" ? flashForFlags(remembered.flash, quickUIEnabled, lateRushEnabled) : null);
  const [notes, setNotes] = useState<Note[] | null>(remembered && status === "open" && notesUIEnabled ? remembered.notes : null);
  // The speedrun note (docs/notes.md "The speedrun note") rides with the notes; drawn only while speedrunUI is on.
  const [speedrun, setSpeedrun] = useState<SpeedrunNote | null>(remembered && status === "open" && notesUIEnabled ? (remembered.speedrun ?? null) : null);
  // The slop tally (docs/notes.md "The slop tally") rides with the notes too; drawn only while slopTallyUI is on.
  const [slop, setSlop] = useState<SlopTally | null>(remembered && status === "open" && notesUIEnabled ? (remembered.slop ?? null) : null);
  // The house's week (docs/house-week.md) rides with the notes too: the theme's banner and the goal's sign.
  const [week, setWeek] = useState<HouseWeekSummary | null>(remembered && status === "open" && notesUIEnabled ? (remembered.week ?? null) : null);
  // The notice board's lead (src/components/WeeklyWriting.tsx, shared/weekly.ts weeklyLead): this
  // week's scroll in progress while the household writes it, else the finished scroll once seen.
  // Its lines are read again with every board load (each brings new notes).
  const weeklyBoard = useWeeklyBoard({ enabled: status === "open" && weeklyUIEnabled && notesUIEnabled, peonName, role, finished: weekly, seen: weeklySheet.seen, refreshKey: notes });
  const [error, setError] = useState<string | null>(null);
  // Done tab pagination (F4/T7): the board's own request returns only the first page; set when
  // that page came back full, so "Show older" appends the next one via api.tasks.list.
  const [nextBefore, setNextBefore] = useState<string | null>(remembered && status === "done" ? remembered.next_before : null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [award, setAward] = useState(() => (status === "open" ? takeLastAward() : null));
  const dismissAward = useCallback(() => setAward(null), []);
  // A plain word from the screen that sent the peon here ("Task added"), shown whatever the flags.
  const [notice, setNotice] = useState(() => (status === "open" ? takeLastNotice() : null));
  const dismissNotice = useCallback(() => setNotice(null), []);
  useEffect(() => { if (status !== "open" || !bountyUIEnabled) dismissAward(); }, [status, bountyUIEnabled, dismissAward]);
  const now = useMinuteTick();
  // The Done board is one tap from the Hall: refresh its saved months behind (shared/hall-memory.ts),
  // so the crown card opens on this month's answer, not last visit's.
  useEffect(() => { if (status === "done") void hall.warm(deviceTimezone()); }, [status]);
  // The peon says the gold rush line once per rush, the first time the banner shows it (src/audio.ts).
  useEffect(() => {
    if (flash?.active) announceGoldRush(flash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash?.active?.id, flash?.claimed]);

  // Tasks this device had never shown when they arrived during this visit to the board. They
  // sit at the top with a "New" pill for the whole visit (so the list never reshuffles under a
  // reading peon) and are recorded as seen once their card has been in view; the next visit
  // sorts them normally. See src/seen.ts.
  const pinnedRef = useRef<Set<string>>(new Set());
  const seenRef = useRef<Set<string> | null>(null);
  // perf.board: time from this visit starting (mount, or the status tab changing) to the first
  // successful list render. boardReportedRef guards against live refreshes re-reporting.
  const boardStartRef = useRef(performance.now());
  const boardReportedRef = useRef(false);
  // The To do and Done tabs share this component, so a visit ends when the status changes, not
  // only on unmount: the cleanup runs for both.
  useEffect(() => () => { pinnedRef.current = new Set(); }, [status]);
  const noteArrivals = (open: Task[]) => {
    const seen = seenRef.current ?? readSeen();
    if (seen === null) {
      // First launch with this build: everything already on the board counts as seen.
      seenRef.current = new Set(open.map((t) => t.id));
      writeSeen(seenRef.current);
      return;
    }
    for (const id of unseenIds(open, seen, peonName)) pinnedRef.current.add(id);
    seenRef.current = pruneSeen(seen, open, peonName);
    writeSeen(seenRef.current);
  };
  const markSeen = (id: string) => {
    const seen = seenRef.current;
    if (!seen || seen.has(id)) return;
    seen.add(id);
    writeSeen(seen);
  };

  // Load now, then again whenever the server reports a change; only the first load shows a spinner.
  // One request (api.board.summary, T5) instead of five, all read from one D1 batch server-side;
  // claims/flash/notes are opt-in query flags so the Done tab (no flash, no notes) and a
  // household with economy/quick/notes off don't pay for statements they won't use.
  const reload = useLive(
    async (initial) => {
      if (initial) {
        boardStartRef.current = performance.now();
        boardReportedRef.current = false;
        // Paint the list this board showed last time (src/board-cache.ts) and refresh behind it,
        // so switching tabs never drops to a skeleton; only a board never seen this visit does.
        // Its cards rise as they would from the network (owner, 2026-10-01: the entrance "only plays the first time").
        const remembered = recallBoard(status);
        setTasks(remembered ? remembered.tasks : null);
        setError(null);
      }
      const since = startOfThisMonth();
      const wantFlash = status === "open" && (quickUIEnabled || lateRushEnabled);
      const wantNotes = status === "open" && notesUIEnabled;
      if (initial) {
        // Everything above the list comes from memory too (the crown, the notes, the rush banner,
        // the Done tab's paging), so the screen paints whole and the fetch changes nothing visible
        // unless the data moved. Otherwise those blocks arrived a beat after the cards and shifted them.
        const remembered = recallBoard(status);
        setWinner(remembered?.top ?? null);
        setCrown(remembered?.crown);
        setStandings(remembered?.standings ?? null);
        setRecap(recapOf(remembered));
        setNotes(remembered && wantNotes ? remembered.notes : null);
        setSpeedrun(remembered && wantNotes ? (remembered.speedrun ?? null) : null);
        setSlop(remembered && wantNotes ? (remembered.slop ?? null) : null);
        setWeek(remembered && wantNotes ? (remembered.week ?? null) : null);
        setFlash(remembered && wantFlash ? flashForFlags(remembered.flash, quickUIEnabled, lateRushEnabled) : null);
        setNextBefore(remembered && status === "done" ? remembered.next_before : null);
      }
      const opts = {
        status,
        name: peonName,
        since,
        prev: status === "done" ? startOfLastMonth() : null,
        claims: economyUIEnabled,
        flash: wantFlash,
        notes: wantNotes,
      };
      try {
        // On the very first load, app.tsx may already have started this exact request in parallel
        // with auth/me (T8, docs/performance.md C2, src/prefetch.ts); use that instead of firing a
        // second one. Any later reload (a live change, or "Try again") always fetches fresh.
        const prefetched = initial ? takePrefetchedBoard(boardRequestKey(opts)) : null;
        const summary = await (prefetched ?? api.board.summary(opts));
        rememberBoard(status, summary);
        if (status === "open") noteArrivals(summary.tasks);
        setTasks(summary.tasks);
        // Every reload (the initial load and every live-triggered refresh alike) replaces the list
        // with the server's first page: the simplest correct behaviour, at the cost of re-folding
        // any older rows a "Show older" tap had appended. Good enough since a live change means the
        // data moved anyway.
        setNextBefore(status === "done" ? summary.next_before : null);
        setError(null);
        if (status === "open") setOpenCount(summary.tasks.length);
        if (!boardReportedRef.current) {
          boardReportedRef.current = true;
          recordBoard(status, performance.now() - boardStartRef.current);
        }
        setWinner(summary.top);
        setCrown(summary.crown);
        setStandings(summary.standings ?? null);
        // The account sheet reads your own numbers from here instead of asking again.
        setAccountStats({ name: peonName, me: summary.me, owedCount: economyUIEnabled ? summary.owed_count : null });
        setFlash(wantFlash ? flashForFlags(summary.flash, quickUIEnabled, lateRushEnabled) : null);
        setNotes(wantNotes ? summary.notes : null);
        setSpeedrun(wantNotes ? (summary.speedrun ?? null) : null);
        setSlop(wantNotes ? (summary.slop ?? null) : null);
        setWeek(wantNotes ? (summary.week ?? null) : null);
        // The house goal's celebration, once per device per week, once the goal is reached.
        if (wantNotes && (summary.flags.houseGoalUI || houseGoalUIEnabled)) offerGoal(summary.week);
        // Rides along in the same batch instead of useHouseholdFlagsSync's own /api/flags call.
        applyHouseholdFlags(summary.flags);
        setRecap(recapOf(summary));
        // The interstitial, once per app open, on the server's word and only with the flag on
        // (read after the flags above, so a cold load uses this summary's own switches).
        offerRecap(recapUIEnabled(), summary);
      } catch (e) {
        if (initial) setError(e instanceof Error ? e.message : "Could not load");
      }
    },
    [status, peonName, economyUIEnabled, quickUIEnabled, lateRushEnabled, notesUIEnabled, recapPreview],
  );

  // "Show older" (F4/T7): fetches the next page of the done list and appends it. Whether another
  // page follows after that is inferred from this page's length (the plain GET /api/tasks route
  // has no next_before of its own), matching how the server decided to hand back a next_before.
  const loadOlder = useCallback(async () => {
    if (!nextBefore || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const older = await api.tasks.list("done", { before: nextBefore, limit: DONE_PAGE_SIZE });
      setTasks((prev) => (prev ? [...prev, ...older] : older));
      setNextBefore(older.length >= DONE_PAGE_SIZE ? (older[older.length - 1]?.done_at ?? null) : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load");
    } finally {
      setLoadingOlder(false);
    }
  }, [nextBefore, loadingOlder]);

  // A card that has been at least 60 percent inside the scroller counts as seen, checked shortly
  // after each load and after each scroll settles. Plain geometry rather than an
  // IntersectionObserver, which stays silent in a hidden tab and would leave cards unseen there.
  // Cards that stay below the fold or off screen keep their "New" pill for the next visit.
  // The same check, on either board, stacks a fanned pile again once the list has been scrolled up
  // until the pile's second card is wholly out of view below (owner's ruling 2026-09-30,
  // shared/nextUp.ts shouldRestack), the way a tap on the open tab stacks it; not while that tap's
  // scroll to the top is under way, since it gathers the pile when that is over.
  const contentRef = useRef<HTMLElement>(null);
  // The fold (src/board-fold.ts, shared/board-fold.ts): the open board rests with Next up at the top
  // and the notice board above it, a heavy pull away; a note ringing for this person starts it revealed.
  const foldRef = useRef<HTMLDivElement>(null);
  const foldOn = notesUIEnabled && status === "open";
  const ringingHere = foldOn && !!notes?.some((n) => cardRinging(n, peonName, Math.max(now, Date.now())));
  const foldHint = useBoardFold(contentRef, foldRef, foldOn, ringingHere);
  // A name tag's push opens the board at what the peon was tagged on (owner, 2026-10-19: "Clicking on
  // the notification should navigate the user to the location they've been tagged in"): the note
  // opened with the notice board in view, the scroll in progress in view, or the finished week's
  // sheet; a note or week that has gone is just the board. Once, then the address goes back to "#/".
  const [openNote, setOpenNote] = useState<string | null>(null);
  const focusKey = status === "open" && focus ? JSON.stringify(focus) : null;
  const focusDone = useRef<string | null>(null);
  useEffect(() => {
    if (!focusKey || focusDone.current === focusKey) return;
    const act = boardFocusAction(focus, {
      notes: notesUIEnabled ? notes : [],
      lead: weeklyBoard.leadData,
      finishedWeek: weekly?.week ?? null,
      loaded: !weeklyUIEnabled || !notesUIEnabled || weeklyBoard.loaded,
    });
    if (act.do === "wait") return;
    focusDone.current = focusKey;
    if (act.do === "note") {
      setOpenNote(act.id);
      foldHint.reveal();
    } else if (act.do === "reveal") {
      foldHint.reveal();
    } else if (act.do === "sheet" && weekly && pendingWeekly()?.week !== weekly.week) {
      previewWeekly(weekly);
    }
    history.replaceState(null, "", `${location.pathname}${location.search}#/`);
  }, [focusKey, notes, weeklyBoard.leadData, weeklyBoard.loaded, weekly, notesUIEnabled, weeklyUIEnabled]);
  const pileGather = useRef<((by: GatherBy) => void) | null>(null);
  // Back from a task (or any screen on this board) lands where the peon was reading; a tab press
  // starts at the top as before. The scroll is restored a frame after the board painted from memory
  // (src/board-cache.ts); a board with nothing remembered shows its placeholder and starts at the top.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const restore = takeBoardScroll(status, previousRouteName());
    let frame = 0;
    if (restore !== null && remembered) frame = requestAnimationFrame(() => { root.scrollTop = restore; });
    let top = root.scrollTop;
    const track = () => { top = root.scrollTop; };
    root.addEventListener("scroll", track, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener("scroll", track);
      rememberBoardScroll(status, top);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  useEffect(() => {
    const root = contentRef.current;
    const seeing = status === "open" && !!tasks;
    if (!root || (!seeing && !fanned)) return;
    let timer = 0;
    const check = () => {
      timer = 0;
      const view = root.getBoundingClientRect();
      if (fanned && pileGather.current && !scrollingToTop()) {
        const second = root.querySelector<HTMLElement>(".pile[data-fanned] > .pile-slot:nth-child(2)");
        // The tab bar sits over the scroller's foot (and the safe area under it): not visible.
        const bar = document.querySelector(".tabbar")?.getBoundingClientRect();
        const viewBottom = bar && bar.height > 0 ? Math.min(view.bottom, bar.top) : view.bottom;
        if (shouldRestack({ secondTop: second ? second.getBoundingClientRect().top : null, viewBottom })) pileGather.current("scroll");
      }
      if (!seeing) return;
      root.querySelectorAll<HTMLElement>(".task-card[data-id]").forEach((el) => {
        // A card under the pile's top one shows only a strip of its frame: not seen yet.
        if (el.closest(".pile-under")) return;
        const r = el.getBoundingClientRect();
        const shown = Math.min(r.bottom, view.bottom) - Math.max(r.top, view.top);
        if (r.height > 0 && shown / r.height >= 0.6 && el.dataset.id) markSeen(el.dataset.id);
      });
    };
    const schedule = () => {
      if (!timer) timer = window.setTimeout(check, 150);
    };
    schedule();
    root.addEventListener("scroll", schedule, { passive: true });
    return () => {
      window.clearTimeout(timer);
      root.removeEventListener("scroll", schedule);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tasks, fanned, restShown]);

  // The open board's order (shared/nextUp.ts liftNew): urgent and luxury tasks first, then the tasks
  // new to this device, then the rest; within each group the server's order stands. Next up's
  // partition puts the chores back above the luxury posts (owner, 2026-10-06).
  const ordered = !tasks ? null : status === "open" ? liftNew(tasks, pinnedRef.current) : tasks;
  // Next up (shared/nextUp.ts partitionBoard, the rule both clients use): on the open board every
  // open chore under the heading, then the hand-added tasks waiting in a pile of three until it fans
  // out; on the Done board the two most recent in full and the rest of what is loaded in the same
  // pile. With nothing to stack the list shows as it is.
  // Tasks new to this device (the "New" pill) show in full under the chores, above the pile.
  // Yours first (shared/yours.ts): out of Next up and the pile, in the board's order; Next up's rule runs on the rest.
  const split = ordered ? splitYours(ordered, peonName, now, { on: yoursUIEnabled && status === "open", roulette: rouletteUIEnabled, claims: claimUIEnabled }) : null;
  const yours = split?.yours ?? [];
  const rest = split?.rest ?? [];
  // In the pile the tasks someone else is on or was picked for wait below the rest (owner, 2026-10-12).
  const next = split && focusUIEnabled ? partitionBoard(rest, status, (t) => pinnedRef.current.has(t.id), { me: peonName, now, roulette: rouletteUIEnabled, claims: claimUIEnabled }) : null;
  const piled = !!next?.collapsed;
  // The tasks after the pile, once it has fanned out: CARDS_PER_FRAME more each frame until all are
  // in. "Show older" appending while fanned only adds to them.
  const restTotal = next && piled && fanned ? Math.max(0, next.waiting.length - PILE_SIZE) : 0;
  useEffect(() => {
    if (restShown >= restTotal) return;
    const frame = requestAnimationFrame(() => setRestShown((n) => Math.min(restTotal, n + CARDS_PER_FRAME)));
    return () => cancelAnimationFrame(frame);
  }, [restShown, restTotal]);

  // The tour's board moments (src/tour-moments.ts): on every render of the open board with what it
  // shows changed, the first open's welcome, else a job just finished, else what the board shows
  // for the first time. After paint and once the list is in, so the cards find their targets (a
  // board moment chosen before the list arrived would leave Next up out until the next open).
  useEffect(() => {
    if (status === "open" && (tasks !== null || error !== null)) {
      playBoardMoments();
      // After the moments, so a tour that opens now is already showing when What's new may show.
      whatsNewBoardLoaded();
      weeklyBoardLoaded();
      cheerBoardLoaded();
      // Task roulette's wheel waits for the board too (src/roulette.ts).
      rouletteBoardLoaded();
    }
  }, [status, tasks, notes, error, focusUIEnabled, notesUIEnabled]);

  return (
    <>
      {/* The toast is fixed to the window, so it sits outside the scroller: a board returned to slides
          its scroller in (src/app.tsx useSlide), and the toast must not ride along. */}
      {bountyUIEnabled && status === "open" && award !== null && <AwardToast gold={award} onDismiss={dismissAward} />}
      {status === "open" && notice !== null && !(bountyUIEnabled && award !== null) && <AwardToast gold={null} text={notice} onDismiss={dismissNotice} />}
      {/* A line written on the weekly scroll earns its gold (src/components/WeeklyWriting.tsx), said the same way. */}
      {bountyUIEnabled && status === "open" && award === null && weeklyBoard.award !== null && <AwardToast gold={weeklyBoard.award} text={SCROLL_GOLD_TOAST} onDismiss={weeklyBoard.dismissAward} />}
      <main class="content content-wide card-grid" ref={contentRef}>
        {status === "done" && <PeonOfTheMonth header={crownHeader({ crown, top: winner })} standings={standings} loading={tasks === null} />}
        {status === "done" && showsRecapControl(recapUI, recap) && <RecapRow recap={recap} />}
        {status === "done" && galleryUIEnabled && <DoneViewToggle view={doneView} onChange={setDoneView} />}
        {foldOn && (
          // Above the fold: the scroller starts under it and the board trails a pull down (src/board-fold.ts).
          <div class="notice-fold" ref={foldRef} data-above-fold="">
            <PinnedNotes peonName={peonName} role={role} now={now} notes={notes} lead={weeklyBoard.lead} openNote={openNote} speedrun={speedrunUIEnabled ? speedrun : null} slop={slopTallyUIEnabled ? slop : null} week={week && (themeWeekUIEnabled || houseGoalUIEnabled) ? week : null} showTheme={themeWeekUIEnabled} showGoal={houseGoalUIEnabled} />
          </div>
        )}
        {/* The weekly scroll's composer: a sheet over the board, portalled out of it (src/components/ComposerSheet.tsx). */}
        {foldOn && weeklyBoard.sheet}
        {status === "open" && (quickUIEnabled || lateRushEnabled) && <FlashBanner flash={flash} now={now} peonName={peonName} />}
        {error && <ErrorBox message={error} onRetry={tasks === null ? reload : undefined} />}
        {!error && tasks === null && <Spinner />}
        {tasks && tasks.length === 0 && status === "open" && peonMoodsEnabled && <EmptyBoard />}
        {tasks && tasks.length === 0 && !(status === "open" && peonMoodsEnabled) && (
          <Empty>{status === "open" ? "Nothing need doing. Add a task with the + button." : "No completed tasks yet."}</Empty>
        )}
        {gallery && tasks && tasks.length > 0 && <DoneGallery tasks={galleryTasks(tasks)} looks={remembered?.looks} />}
        {ordered && !gallery && (() => {
          const pinned = pinnedRef.current;
          const card = (t: Task) => <TaskCard key={t.id} task={t} now={now} flash={flash} isNew={pinned.has(t.id)} peonName={peonName} />;
          // The fold's hint (owner, 2026-10-05: "There should be a little up arrow with a scroll icon at
          // the top", "In the same line as next up but centered"): centred across the first heading's row
          // (Yours when it shows, else Next up's), out of its flow so the row keeps its height, whenever
          // there is a fold. With no heading at all (no chore, nothing yours), a row of the art's height
          // holds it in the same place.
          const hint = foldHint.hint && (
            // The arrow always points up and the tap always goes up to the board (owner, 2026-10-05: "Remove the rotating arrow").
            <button type="button" class="fold-hint" aria-label="Up to the notice board" onClick={foldHint.reveal}>
              <img class="fold-hint-art" src="/ui/icon-fold-hint.png" width={44} height={20} alt="" aria-hidden="true" />
            </button>
          );
          // Yours (docs/yours.md): the tasks assigned to you or that you are on, first, under their own heading.
          const yoursRow = yours.length > 0 && (
            <div class="next-up-row yours-row">
              <h2 class="board-heading yours">{YOURS_HEADING}</h2>
              {hint}
            </div>
          );
          const nextUpRow = (heading: boolean) =>
            (heading || (foldOn && ordered.length > 0 && yours.length === 0)) && (
              <div class={heading ? "next-up-row" : "next-up-row next-up-spacer"}>
                {heading && <h2 class="board-heading next-up">Next up</h2>}
                {yours.length === 0 && hint}
              </div>
            );
          // Under Yours the rest keeps its heading even with nothing in full, so the group has an end.
          const restHeads = restHeading(!!next?.heading, yours.length, rest.length);
          if (!next || (!next.heading && !piled)) return <>{yoursRow}{yours.map(card)}{nextUpRow(restHeads)}{rest.map(card)}</>;
          return (
            <>
              {yoursRow}
              {yours.map(card)}
              {nextUpRow(restHeads)}
              {next.full.map(card)}
              {!piled && next.waiting.map(card)}
              {piled && (
                <NextUpPile board={status} pile={next.pile} count={next.waiting.length} fanned={fanned} onFan={fan} onGather={gather} gatherRef={pileGather} scrollerRef={contentRef} card={card} />
              )}
              {/* No count under the pile on either board: the tab counts (owner, 2026-10-04, "14 more" over
                  "15 tasks waiting", then the Done board's "48 more done" under "47 more": "Two issues
                  here"). On both boards the hint alone stays there, in the caption's paragraph so it
                  fades with the pull, until the first fan. */}
              {piled && !fanned && !pileHintSeen && next.waiting.length > 1 && (
                <p class="pile-caption">
                  <span class="pile-hint pile-hint-alone">Pull past the end to fan them out.</span>
                </p>
              )}
              {piled && fanned && next.waiting.slice(PILE_SIZE, PILE_SIZE + restShown).map(card)}
            </>
          );
        })()}
        {/* Stacked, the pile holds the older pages too; "Show older" follows the fanned list. */}
        {status === "done" && nextBefore && (gallery || !(piled && !fanned)) && (
          <button type="button" class="board-fold" onClick={loadOlder} disabled={loadingOlder}>
            <span class="board-fold-caret" aria-hidden="true">▾</span>
            <BusyLabel busy={loadingOlder} label="Loading…">Show older</BusyLabel>
          </button>
        )}
      </main>
      {/* The add button is away while the notice board is open, the hint's own open state (owner,
          2026-10-05: "The add task button should disappear when on the note board"): faded out in
          place, no tap, no keyboard stop. */}
      <TabBar current={status === "open" ? "open" : "done"}>
        {status === "open" && (
          <a class="fab"
            data-away={foldHint.open ? "" : undefined}
            href={href({ name: "newTask" })}
            aria-label="Add task"
            aria-hidden={foldHint.open ? "true" : undefined}
            tabIndex={foldHint.open ? -1 : undefined}
          >
            <PixelIcon name="add" />
          </a>
        )}
      </TabBar>
    </>
  );
}
